// pages/auth/ProfilePage.jsx
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaRegEye } from "react-icons/fa";
import { BiSolidAddToQueue } from "react-icons/bi";

import UserEditModal from '../../components/auth/UserEditModal';

import { MdEdit } from 'react-icons/md';
import moment from 'moment';
import { useAuth } from '../../context/UserContext';
import utils from '../../utils';
import AuthSideBar from '../../components/auth/AuthSideBar';
import vendorServices from '../../services/VendorSevices';
import toast from 'react-hot-toast';
import StoreInfo from '../../components/vendor/StoreInfo';

function ProfilePage() {
    const { isAuth, user, store, setStore } = useAuth();
    const navigate = useNavigate();

    // Redirect if not authenticated
    useEffect(() => {
        if (!isAuth) {
            navigate('/auth/login', { replace: true });
        }
    }, [isAuth, navigate]);

    useEffect(() => {
        if (user?.is_vendor && !store) {
            vendorServices.getVendorStore().then(
                res => {
                    console.log(res.data);
                    if (res.data?.length) {
                        setStore(res.data[0])
                    }
                },
                err => {
                    console.log(err);

                }
            )

        }
    }, [user, store, setStore])


    // Wait for auth check & user load
    if (!isAuth) {
        return null; // or a minimal loader if you prefer
    }

    function storeCheck() {
        if (user?.is_verified && !store) {
            toast.success("Ready to Create your Store")
        }
        if (!user?.is_verified && !store) {
            toast.error("You have to be verified by Admin to Create Store")
        }
        navigate('/dashboard/vendor')
    }

    return (
        <main className="container mx-auto px-6 py-4">
            <Helmet>
                <title>Profile - {utils.siteName}</title>
                <meta name="description" content="Manage your account settings and personal information." />
                <meta name="robots" content="noindex" />
            </Helmet>

            <div className="flex flex-col md:flex-row gap-8 lg:gap-12">
                <AuthSideBar />
                <div className="flex-1 space-y-7">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden border border-border-light dark:border-border-dark">
                        <div className="p-6 md:p-8">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                                        My Profile
                                    </h1>
                                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                                        Manage your account settings and personal information.
                                    </p>
                                </div>

                                {/* Edit button + modal trigger */}
                                <label
                                    htmlFor="user-update-modal"
                                    className="mt-4 sm:mt-0 flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors cursor-pointer"
                                >
                                    <MdEdit className="text-base" />
                                    <span>Edit Profile</span>
                                </label>
                            </div>

                            <div className="border-t border-gray-200 dark:border-border-dark pt-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                                    {user?.full_name && (
                                        <div>
                                            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                Full Name
                                            </label>
                                            <p className="mt-1 text-gray-800 dark:text-gray-200 font-medium">
                                                {user.full_name}
                                            </p>
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                            Email Address
                                        </label>
                                        <p className="mt-1 text-gray-800 dark:text-gray-200 font-medium">
                                            {user?.email}
                                        </p>
                                    </div>

                                    {user?.phone_number && (
                                        <div>
                                            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                Phone Number
                                            </label>
                                            <p className="mt-1 text-gray-800 dark:text-gray-200 font-medium">
                                                {user.phone_number}
                                            </p>
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                            Member Since
                                        </label>
                                        <p className="mt-1 text-gray-800 dark:text-gray-200 font-medium">
                                            {user?.creation_time
                                                ? moment(user.creation_time).fromNow()
                                                : '-'}
                                        </p>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2">
                                            Account Type:
                                        </label>
                                        <p className="mt-1 inline-flex flex-wrap items-center gap-2">
                                            {/* Admin */}
                                            {user?.is_admin && (
                                                <span className="px-3 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-xs font-semibold rounded-full">
                                                    Admin
                                                </span>
                                            )}

                                            {/* Superuser (distinct from Admin) */}
                                            {user?.is_superuser && !user?.is_admin && (
                                                <span className="px-3 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 text-xs font-semibold rounded-full">
                                                    Superuser
                                                </span>
                                            )}

                                            {/* Vendor */}
                                            {user?.is_vendor && (
                                                <span className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs font-semibold rounded-full">
                                                    Vendor
                                                </span>
                                            )}

                                            {/* Regular User */}
                                            {!user?.is_admin && !user?.is_superuser && !user?.is_vendor && (
                                                <span className="px-3 py-1 bg-gray-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs font-semibold rounded-full">
                                                    User
                                                </span>
                                            )}

                                            {/* Verified badge */}
                                            {user?.is_verified && (
                                                <span className="text-xs text-green-600 dark:text-green-400 flex items-center">
                                                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                    Verified
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {user?.is_vendor && (
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden border border-border-light dark:border-border-dark">
                            <div className="p-6 md:p-8">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
                                    <div>
                                        <h1 className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">
                                            My Store
                                        </h1>
                                        <p className="text-gray-500 dark:text-gray-400 mt-1">
                                            Your Store Details.
                                        </p>
                                    </div>

                                    <label
                                        onClick={() => storeCheck()}
                                        className="mt-4 sm:mt-0 flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors cursor-pointer"
                                    >
                                        {store ? <FaRegEye/> : <BiSolidAddToQueue/>}
                                        <span>{!store ? 'Add' : 'View'} Store</span>
                                    </label>
                                </div>
                                <div className="border-t border-gray-200 dark:border-border-dark pt-6">
                                    {user?.is_verified && !store && (
                                        <div>
                                            <p>Please Create Your Store. Go To <Link className='underline' to={'/dashboard/vendor'}>Vendor Dashboard</Link>.</p>
                                        </div>
                                    )}
                                    {!user?.is_verified && (
                                        <div>
                                            <p>You have to be Verified By Admin to create a Store as a Vendor.</p>
                                        </div>
                                    )}
                                    {store && (
                                        <StoreInfo/>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal (assumes it uses <input id="user-update-modal" type="checkbox" /> internally) */}
            <UserEditModal />
        </main>
    );
}

export default ProfilePage;